import React, {
  useState,
  useRef,
  useCallback,
  useEffect,
  memo,
  useMemo,
  useLayoutEffect
} from 'react'
import { Heap } from 'heap-js'
import ProjectCard from '@components/project/ProjectCard'
import {
  getProjectCardHeightFromDocumentFrameSize,
  ProjectCardSizeLimits
} from './ProjectCardSize'
import { Maybe, Project } from '@store/graphql/__generated__/schema'
import { ProjectListType } from './ProjectList'

interface ProjectMasonryListProps {
  projects: Array<Maybe<Project>>
  isAdmin: boolean
  projectListType: ProjectListType
}

interface ProjectCardPosition {
  top: number
  left: number
  height: number
}

interface ColumnInfo {
  index: number
  height: number
}

const ProjectMasonryList: React.FC<ProjectMasonryListProps> = ({
  isAdmin,
  projectListType,
  projects
}) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const cachedCardHeights = useRef<{ [uuid: string]: number }>({})
  const maxColumnHeight = useRef<number>(0)
  const [projectCardPositions, setProjectCardPositions] = useState<
    ProjectCardPosition[]
  >([])

  const gap = 10
  const projectCardWidth = ProjectCardSizeLimits.max.width

  const columnCount = useMemo(() => {
    const cols = Math.floor(window.innerWidth / (projectCardWidth + gap))
    return Math.min(cols, 8)
  }, [window.innerWidth])

  const containerWidth = useMemo(
    () => columnCount * projectCardWidth + (columnCount - 1) * gap,
    [columnCount]
  )

  const getCachedCardHeight = useCallback(
    (uuid: string, documentFrameSize: any) => {
      if (cachedCardHeights.current[uuid]) {
        return cachedCardHeights.current[uuid]
      }
      const height = getProjectCardHeightFromDocumentFrameSize(
        documentFrameSize,
        uuid
      )
      cachedCardHeights.current[uuid] = height
      return height
    },
    []
  )

  const calculateProjectCardPositions = useCallback(() => {
    const newProjectCardPositions: ProjectCardPosition[] = []
    const columnHeap = new Heap<ColumnInfo>((a, b) => a.height - b.height)

    const actualColumnCount = Math.min(columnCount, projects.length)

    for (let i = 0; i < actualColumnCount; i++) {
      columnHeap.push({ index: i, height: 0 })
    }

    projects.forEach(project => {
      const shortestColumn = columnHeap.pop()

      if (!shortestColumn) return

      const projectCardHeight = getCachedCardHeight(
        project!.uuid as string,
        project!.documentFrameSize
      )

      const top = shortestColumn.height
      const left = shortestColumn.index * (projectCardWidth + gap)

      shortestColumn.height += projectCardHeight + gap * 1.5
      columnHeap.push(shortestColumn)

      newProjectCardPositions.push({ top, left, height: projectCardHeight })
    })

    let _maxColumnHeight = 0
    for (const column of columnHeap.toArray()) {
      if (column.height > _maxColumnHeight) {
        _maxColumnHeight = column.height
      }
    }

    maxColumnHeight.current = _maxColumnHeight
    setProjectCardPositions(newProjectCardPositions)
  }, [projects, columnCount])

  useEffect(() => {
    calculateProjectCardPositions()
  }, [calculateProjectCardPositions])

  useLayoutEffect(() => {
    const handleResize = () => calculateProjectCardPositions()
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [calculateProjectCardPositions])

  return projectCardPositions.length === 0 ? null : (
    <div
      ref={containerRef}
      style={{
        position: 'relative',
        marginTop: 14,
        width: containerWidth,
        height: maxColumnHeight.current
      }}>
      {projects.map(
        (project, index) =>
          projectCardPositions[index] && (
            <div
              key={`masonry-card-${project!.uuid}`}
              style={{
                position: 'absolute',
                top: projectCardPositions[index]?.top,
                left: projectCardPositions[index]?.left,
                width: projectCardWidth,
                boxSizing: 'border-box'
              }}>
              <ProjectCard
                key={`project-card-${project?.uuid}`}
                uuid={project?.uuid!}
                name={project?.name!}
                imageUrl={project?.imageUrl!}
                documentFrameSize={project?.documentFrameSize}
                getCachedCardHeight={getCachedCardHeight}
                createdAt={project?.insertedAt!}
                projectOwnerUuid={project?.ownerUserUuid}
                public={project?.public}
                featured={project?.featured}
                totalReactions={project?.totalReactions}
                userReactedWith={project?.userReactedWith}
                uniqueReactionTypes={project?.uniqueReactionTypes || []}
                adminEditingEnabled={isAdmin}
                showShareBtn={true}
                showReactions={projectListType !== ProjectListType.CurrentUser}
                editingEnabled={projectListType === ProjectListType.CurrentUser}
                showSharedBadge={
                  projectListType === ProjectListType.CurrentUser
                }
              />
            </div>
          )
      )}
    </div>
  )
}

export default memo(
  ProjectMasonryList,
  (prevProps, nextProps) =>
    prevProps.isAdmin === nextProps.isAdmin &&
    prevProps.projectListType === nextProps.projectListType &&
    prevProps.projects === nextProps.projects
)
